import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Inbox, Mail, Reply, Archive, Search, CheckCheck, MessageSquare,
  ExternalLink, User as UserIcon, Phone, Filter, RefreshCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAdminUi } from "@/context/AdminUiContext";

const CATS = ["", "booking", "payment", "cleaner", "verification", "payouts", "bug", "account", "other"];
const STATUSES = ["", "open", "read", "archived"];

function Chip({ tone = "slate", children }) {
  const tones = {
    teal:  "bg-teal-500/15 text-teal-200 border-teal-400/30",
    amber: "bg-amber-500/15 text-amber-200 border-amber-400/40",
    red:   "bg-red-500/15 text-red-300 border-red-400/40",
    green: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
    slate: "bg-slate-500/15 text-slate-200 border-slate-400/30",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-[0.14em] ${tones[tone]}`}>{children}</span>;
}

export default function AdminSupportInbox() {
  const { openUser, openBooking } = useAdminUi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", category: "", from: "", to: "" });
  const [replyBody, setReplyBody] = useState("");
  const [noteBody, setNoteBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const { data } = await api.get(`/admin/support/tickets${qs ? "?" + qs : ""}`);
      setRows(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load inbox"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/support/tickets/${selectedId}`);
        setDetail(data);
      } catch (e) { toast.error("Failed to load ticket"); }
    })();
  }, [selectedId]);

  const markRead    = async (id) => { await api.post(`/admin/support/tickets/${id}/mark-read`, {});   toast.success("Marked as read");    load(); if (selectedId === id) setSelectedId(id); };
  const markUnread  = async (id) => { await api.post(`/admin/support/tickets/${id}/mark-unread`, {}); toast.success("Marked as unread");  load(); if (selectedId === id) setSelectedId(id); };
  const archive     = async (id) => { await api.post(`/admin/support/tickets/${id}/archive`, {});     toast.success("Archived");           load(); setSelectedId(null); };
  const unarchive   = async (id) => { await api.post(`/admin/support/tickets/${id}/unarchive`, {});   toast.success("Restored");           load(); };
  const addNote     = async () => {
    if (!noteBody.trim() || !detail) return;
    await api.post(`/admin/support/tickets/${detail.ticket.id}/notes`, { note: noteBody.trim() });
    setNoteBody(""); toast.success("Note added");
    const { data } = await api.get(`/admin/support/tickets/${detail.ticket.id}`); setDetail(data);
  };
  const sendReply   = async () => {
    if (replyBody.trim().length < 5 || !detail) return;
    try {
      const { data } = await api.post(`/admin/support/tickets/${detail.ticket.id}/reply`, { body: replyBody.trim() });
      toast.success(data.entry?.delivered ? "Reply sent to " + data.entry.to_email : "Reply saved (email delivery failed — check logs)");
      setReplyBody("");
      const { data: fresh } = await api.get(`/admin/support/tickets/${detail.ticket.id}`); setDetail(fresh); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reply failed"); }
  };

  const unreadCount = useMemo(() => rows.filter(r => r.status === "open").length, [rows]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 text-slate-100" data-testid="admin-support-inbox">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300">Admin · Messages</div>
          <h1 className="font-display font-extrabold text-2xl mt-1 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-teal-300" /> Support inbox
            {unreadCount > 0 && <span data-testid="support-unread-count" className="ml-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-200 border border-red-400/40 font-mono text-[11px]">{unreadCount} unread</span>}
          </h1>
          <p className="text-sm text-slate-400 mt-1">Every "Contact CLEANBAZ" message from clients lands here.</p>
        </div>
        <button onClick={load} data-testid="support-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-slate-200 hover:border-teal-400 text-xs font-mono uppercase tracking-[0.16em]"><RefreshCcw className="w-3.5 h-3.5" />Refresh</button>
      </div>

      {/* Filters */}
      <Card className="mb-4 bg-white/[0.03] border-white/10">
        <CardContent className="p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div className="col-span-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-3 text-slate-400" />
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="support-search" placeholder="Search subject / message / client" className="w-full pl-7 bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100" />
            </div>
          </div>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="support-filter-status" className="bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100">{STATUSES.map(s => <option key={s} value={s}>{s || "any status"}</option>)}</select>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} data-testid="support-filter-category" className="bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100">{CATS.map(c => <option key={c} value={c}>{c || "any category"}</option>)}</select>
          <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="support-filter-from" className="bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100" />
          <div className="flex gap-1">
            <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="support-filter-to" className="flex-1 bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100" />
            <button onClick={load} data-testid="support-apply" className="px-3 rounded-md bg-teal-500 hover:bg-teal-400 text-slate-950 text-xs font-mono font-semibold"><Filter className="w-3.5 h-3.5" /></button>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        {/* List */}
        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-slate-400">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="support-empty">No messages match.</div>
            ) : (
              <ul>
                {rows.map((t) => (
                  <li key={t.id}>
                    <button
                      onClick={() => setSelectedId(t.id)}
                      data-testid={`support-row-${t.id}`}
                      className={`w-full text-left border-b border-white/5 p-3 hover:bg-white/[0.03] ${selectedId === t.id ? "bg-teal-500/[0.06] border-l-2 border-l-teal-400" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm text-slate-100 truncate flex-1">
                          {t.status === "open" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-400 mr-1.5" />}
                          {t.subject}
                        </div>
                        <Chip tone={t.status === "open" ? "amber" : t.status === "archived" ? "slate" : "green"}>{t.status}</Chip>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.client_full_name} · {t.client_email}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Chip tone="slate">{t.category}</Chip>
                        {t.thread_count > 1 && <Chip tone="teal"><MessageSquare className="w-3 h-3" />thread · {t.thread_count}</Chip>}
                        <span className="text-[10px] font-mono text-slate-400 ml-auto">{new Date(t.created_at).toLocaleString()}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Detail */}
        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-5 max-h-[70vh] overflow-y-auto">
            {!detail ? (
              <div className="text-slate-500 text-sm" data-testid="support-detail-empty">Select a message from the left.</div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-display font-bold text-slate-100" data-testid="support-detail-subject">{detail.ticket.subject}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">{new Date(detail.ticket.created_at).toLocaleString()}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Chip tone="slate">{detail.ticket.category}</Chip>
                      <Chip tone={detail.ticket.status === "open" ? "amber" : detail.ticket.status === "archived" ? "slate" : "green"}>{detail.ticket.status}</Chip>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {detail.ticket.status === "open" ? (
                      <button onClick={() => markRead(detail.ticket.id)} data-testid="support-mark-read" className="px-2.5 py-1 text-[11px] rounded-md border border-teal-400/40 text-teal-200 hover:bg-teal-500/10 inline-flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" />Mark read</button>
                    ) : detail.ticket.status !== "archived" ? (
                      <button onClick={() => markUnread(detail.ticket.id)} data-testid="support-mark-unread" className="px-2.5 py-1 text-[11px] rounded-md border border-white/10 text-slate-200 hover:border-teal-400 inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />Mark unread</button>
                    ) : null}
                    {detail.ticket.status !== "archived" ? (
                      <button onClick={() => archive(detail.ticket.id)} data-testid="support-archive" className="px-2.5 py-1 text-[11px] rounded-md border border-white/10 text-slate-300 hover:border-red-400 hover:text-red-200 inline-flex items-center gap-1"><Archive className="w-3.5 h-3.5" />Archive</button>
                    ) : (
                      <button onClick={() => unarchive(detail.ticket.id)} data-testid="support-unarchive" className="px-2.5 py-1 text-[11px] rounded-md border border-white/10 text-slate-300 hover:border-teal-400 inline-flex items-center gap-1"><Archive className="w-3.5 h-3.5" />Unarchive</button>
                    )}
                  </div>
                </div>

                {/* Client card */}
                <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3 flex flex-wrap gap-3 items-center">
                  <button onClick={() => openUser(detail.ticket.client_id)} data-testid="support-open-client" className="text-left hover:text-teal-300 flex-1 min-w-[200px]">
                    <div className="text-sm text-slate-100 inline-flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" />{detail.ticket.client_full_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{detail.ticket.client_email}</div>
                  </button>
                  <div className="text-xs text-slate-400"><Mail className="w-3 h-3 inline mr-1" />reply to <span className="font-mono text-slate-200">{detail.ticket.reply_email}</span></div>
                  {detail.ticket.phone && <div className="text-xs text-slate-400"><Phone className="w-3 h-3 inline mr-1" /><span className="font-mono text-slate-200">{detail.ticket.phone}</span></div>}
                  {detail.related_job && (
                    <button onClick={() => openBooking(detail.related_job.id)} data-testid="support-open-booking" className="text-xs text-teal-300 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />booking {detail.related_job.id.slice(0, 8)}</button>
                  )}
                </div>

                {/* Thread history */}
                <div className="mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300 mb-1">Conversation ({detail.thread.length})</div>
                  <div className="space-y-2" data-testid="support-thread">
                    {detail.thread.map((m) => (
                      <div key={m.id} className="rounded-lg border border-white/10 p-3">
                        <div className="text-[10px] font-mono text-slate-400">{new Date(m.created_at).toLocaleString()} · from client</div>
                        <div className="text-sm text-slate-200 whitespace-pre-wrap mt-1">{m.message}</div>
                        {(m.replies || []).length > 0 && (
                          <div className="mt-2 pl-3 border-l-2 border-teal-400/40 space-y-2">
                            {m.replies.map((r, i) => (
                              <div key={i} className="text-xs">
                                <div className="text-[10px] font-mono text-slate-400">{new Date(r.at).toLocaleString()} · admin reply to {r.to_email} · {r.delivered ? "delivered" : "not delivered"}</div>
                                <div className="text-slate-200 whitespace-pre-wrap mt-1">{r.body}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Admin notes */}
                <div className="mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300 mb-1">Internal admin notes</div>
                  <div className="space-y-1">
                    {(detail.ticket.admin_notes || []).map((n, i) => (
                      <div key={i} className="text-xs bg-slate-900/60 border border-white/5 rounded p-2">
                        <div className="text-[10px] font-mono text-slate-400">{n.actor_email} · {new Date(n.at).toLocaleString()}</div>
                        <div className="text-slate-200 whitespace-pre-wrap mt-0.5">{n.note}</div>
                      </div>
                    ))}
                    {(detail.ticket.admin_notes || []).length === 0 && <div className="text-xs text-slate-500">No notes yet.</div>}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} data-testid="support-note-input" rows={2} className="flex-1 bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100" placeholder="Internal note (never emailed to the client)…" />
                    <button onClick={addNote} data-testid="support-note-submit" className="px-3 rounded-md bg-teal-500 hover:bg-teal-400 text-slate-950 text-xs font-mono font-semibold">Add note</button>
                  </div>
                </div>

                {/* Reply */}
                {detail.ticket.status !== "archived" && (
                  <div className="mt-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300 mb-1">Reply to {detail.ticket.reply_email}</div>
                    <textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} data-testid="support-reply-body" rows={4} className="w-full bg-slate-900 border border-white/10 rounded-md px-3 py-2 text-sm text-slate-100" placeholder="Type your reply. It will be emailed to the client's reply email." />
                    <div className="mt-2 flex justify-end">
                      <button onClick={sendReply} disabled={replyBody.trim().length < 5} data-testid="support-reply-send" className="px-4 py-2 rounded-md bg-teal-500 hover:bg-teal-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 text-xs font-mono uppercase tracking-[0.16em] font-semibold inline-flex items-center gap-1.5"><Reply className="w-3.5 h-3.5" />Send reply</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
